import React from 'react';
import clsx from 'clsx';
import styles from '@danske/sapphire-css/components/spinner/spinner.module.css';
import {
  SapphireStyleProps,
  useThemeCheck,
  useSapphireStyleProps,
} from '../../utils';
import { GlobalDomAttributes } from '../../utils/types';

export type SpinnerProps = {
  /**
   * @default 'm'
   */
  size?: '3xs' | '2xs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  /**
   * The color variant of the spinner. Using `default` will use predefined colors, while `inherit` will use the text color.
   * @default 'default'
   */
  variant?: 'default' | 'inherit';
} & SapphireStyleProps &
  GlobalDomAttributes &
  ({ 'aria-labelledby': string } | { 'aria-label': string });

/**
 * Animated element that show that some data is still loading or some process
 * is in progress. The hidden label is required for accessibility reasons.
 */
export const Spinner = ({
  size = 'md',
  variant = 'default',
  ...props
}: SpinnerProps): JSX.Element => {
  useThemeCheck();

  const { styleProps } = useSapphireStyleProps(props);

  /**
   * There is @react-aria/progress but because we only have a "indeterminate"
   * spinner, we only need one prop, the 'role' attr.
   * Therefore, it seemed like an overkill to use react-aria here.
   */

  return (
    <svg
      role="progressbar"
      aria-label={'aria-label' in props ? props['aria-label'] : undefined}
      aria-labelledby={
        'aria-labelledby' in props ? props['aria-labelledby'] : undefined
      }
      className={clsx(
        styleProps.className,
        styles['sapphire-spinner'],
        {
          [styles['sapphire-spinner--inherit']]: variant === 'inherit',
        },
        size !== 'md' ? styles[`sapphire-spinner--${size}` as const] : ''
      )}
      style={styleProps.style}
      viewBox="0 0 16 16"
    >
      <path
        className={styles['sapphire-spinner__track']}
        d="M10.3993 2.13882C10.3295 2.39914 10.4641 2.66858 10.7034 2.79263C11.2238 3.06246 11.7123 3.41501 12.1489 3.85159C14.4401 6.14281 14.4399 9.85728 12.1489 12.1483C9.85783 14.4393 6.14335 14.4395 3.85214 12.1483C3.41556 11.7117 3.06301 11.2232 2.79317 10.7028C2.66913 10.4635 2.39969 10.329 2.13937 10.3987C1.82795 10.4821 1.65738 10.8194 1.80178 11.1077C2.1261 11.7551 2.55778 12.3624 3.09789 12.9026C5.80564 15.6103 10.1955 15.6101 12.9031 12.9026C15.6107 10.195 15.6109 5.80509 12.9031 3.09734C12.363 2.55723 11.7556 2.12555 11.1082 1.80123C10.82 1.65683 10.4827 1.8274 10.3993 2.13882Z"
      />
      <path
        className={styles['sapphire-spinner__accent']}
        d="M1.72661 8.85304C1.98694 8.78329 2.15316 8.53215 2.14111 8.26291C2.07002 6.67514 2.63971 5.06425 3.85203 3.85193C5.06452 2.63944 6.67527 2.06989 8.26302 2.14101C8.53225 2.15306 8.78337 1.98683 8.85309 1.7265C8.93661 1.41464 8.72921 1.09798 8.4069 1.07913C6.49959 0.96764 4.55516 1.6403 3.09778 3.09768C1.64062 4.55485 0.967813 6.49934 1.07909 8.40676C1.0979 8.72916 1.41467 8.93663 1.72661 8.85304Z"
      />
    </svg>
  );
};
